/**
 * useProfileSkills — reads the user's skills from user_profiles.skills
 * (set during onboarding / account settings) and builds the same
 * UserSkillsContext that useUserSkills produces from a resume.
 *
 * This is the primary skill source; useUserSkills (resume) is a richer
 * fallback that adds past job-title tokens.
 */

import { useEffect, useState } from 'react';

import { supabase } from '../../../lib/supabase';
import { tokenize } from '../components/Jobs/ranking';
import type { UserSkillsContext } from './useUserSkills';

export interface UseProfileSkillsResult {
  ctx:             UserSkillsContext | null;
  skills:          string[];           // raw skill strings (for UI display)
  experienceYears: number | null;      // total years of experience (0 = fresher, null = unknown)
  location:        string | null;      // preferred location from profile (for external job search)
  currentJobTitle: string | null;      // current job title / desired role (search keywords)
  hasProfile:      boolean;
  loading:         boolean;
}

export function useProfileSkills(isSignedIn: boolean): UseProfileSkillsResult {
  const [ctx,             setCtx]             = useState<UserSkillsContext | null>(null);
  const [skills,          setSkills]          = useState<string[]>([]);
  const [experienceYears, setExperienceYears] = useState<number | null>(null);
  const [location,        setLocation]        = useState<string | null>(null);
  const [currentJobTitle, setCurrentJobTitle] = useState<string | null>(null);
  const [hasProfile,      setHasProfile]      = useState(false);
  const [loading,         setLoading]         = useState(false);

  useEffect(() => {
    if (!isSignedIn) {
      setCtx(null);
      setSkills([]);
      setHasProfile(false);
      setLocation(null);
      setCurrentJobTitle(null);
      return;
    }

    let mounted = true;
    setLoading(true);

    const run = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session || !mounted) return;

        const { data: row } = await supabase
          .from('user_profiles')
          .select('skills, current_job_title, headline, experience_years, location')
          .eq('id', session.user.id)
          .maybeSingle();

        if (!mounted) return;

        const rawSkills: string[] =
          Array.isArray(row?.skills) ? (row!.skills as string[]) : [];

        const expYears: number | null =
          typeof row?.experience_years === 'number' ? row.experience_years : null;

        const loc: string | null =
          typeof row?.location === 'string' && row.location.trim() ? row.location.trim() : null;

        const jobTitle: string | null =
          typeof row?.current_job_title === 'string' && row.current_job_title.trim()
            ? row.current_job_title.trim()
            : null;

        if (rawSkills.length === 0) {
          setHasProfile(false);
          setExperienceYears(expYears);
          setLocation(loc);
          setCurrentJobTitle(jobTitle);
          return;
        }

        const skillSet = new Set<string>();
        rawSkills.forEach(s => tokenize(s).forEach(t => skillSet.add(t)));

        // Also add tokens from current role / headline for title matching
        const titleSet = new Set<string>();
        if (row?.current_job_title) tokenize(row.current_job_title).forEach(t => titleSet.add(t));
        if (row?.headline)          tokenize(row.headline).forEach(t => titleSet.add(t));

        if (mounted) {
          setHasProfile(true);
          setSkills(rawSkills);
          setExperienceYears(expYears);
          setLocation(loc);
          setCurrentJobTitle(jobTitle);
          setCtx({ skills: skillSet, titles: titleSet });
        }
      } catch {
        // Best-effort — fail silently
      } finally {
        if (mounted) setLoading(false);
      }
    };

    void run();
    return () => { mounted = false; };
  }, [isSignedIn]);

  return { ctx, skills, experienceYears, location, currentJobTitle, hasProfile, loading };
}
